import { generateInvoicePDF, resolveFirm } from "@/lib/pdf";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "sonner";

/**
 * Build a short plain-text invoice summary suitable for chat/email body.
 * Includes: greeting, invoice #, total, due date (if any), bank details for unpaid.
 */
function buildInvoiceMessage(bill, businessInfo) {
  const firm = resolveFirm(bill, businessInfo);
  const businessName = firm?.name || "Billing";
  const bank = firm?.bank || {};
  const isPaid = bill.status === "paid";

  const lines = [];
  const greetingName = (bill.client_name || "").trim() || "there";
  lines.push(`Dear ${greetingName},`);
  lines.push("");
  lines.push(
    `Please find your invoice ${bill.invoice_no} for ${formatINR(bill.total)}${
      bill.due_date ? `, due ${formatDate(bill.due_date)}` : ""
    }.`
  );

  if (bill.services && bill.services.length > 0) {
    lines.push("");
    lines.push("Services:");
    bill.services.forEach((s) => {
      lines.push(`- ${s.description}: ${formatINR(s.fee)}`);
    });
  }

  if (!isPaid && bank?.account_number) {
    lines.push("");
    lines.push("Payment details:");
    if (bank.account_name) lines.push(`Account: ${bank.account_name}`);
    if (bank.bank_name) lines.push(`Bank: ${bank.bank_name}`);
    lines.push(`A/c No: ${bank.account_number}`);
    if (bank.ifsc) lines.push(`IFSC: ${bank.ifsc}`);
    if (bank.upi) lines.push(`UPI: ${bank.upi}`);
  }

  lines.push("");
  lines.push(`Thanks,\n${businessName}`);
  return lines.join("\n");
}

function cleanPhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D+/g, "");
  // If it looks like a 10-digit Indian number, prepend country code
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

/**
 * Downloads the PDF and opens WhatsApp with a pre-filled invoice message.
 * When the browser's Web Share API supports files (Chrome Android / iOS Safari),
 * uses navigator.share to attach the PDF directly to the WhatsApp share sheet.
 * Otherwise falls back to downloading the PDF + opening a wa.me link.
 * @returns {Promise<boolean>} true if intent opened; false if phone missing.
 */
export async function sendInvoiceViaWhatsApp(bill, client, businessInfo) {
  const phone = cleanPhone(client?.phone);
  if (!phone) {
    toast.error("Client phone is required for WhatsApp. Add it in Edit Details.");
    return false;
  }
  const text = buildInvoiceMessage(bill, businessInfo);

  // Try Web Share API with files (best on mobile — attaches PDF to WhatsApp)
  try {
    const blob = generateInvoicePDF(bill, businessInfo, { returnBlob: true });
    const file = new File([blob], `${bill.invoice_no}.pdf`, { type: "application/pdf" });
    if (
      navigator.canShare &&
      navigator.canShare({ files: [file] }) &&
      typeof navigator.share === "function"
    ) {
      await navigator.share({ files: [file], text, title: bill.invoice_no });
      toast.success("Pick WhatsApp from the share sheet — PDF is already attached");
      return true;
    }
  } catch (err) {
    // User cancelled share or share not available — fall through to fallback
    if (err && err.name === "AbortError") return false;
  }

  // Fallback: download the PDF and open wa.me link (desktop)
  generateInvoicePDF(bill, businessInfo);
  const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  toast.success("PDF downloaded — attach it in the WhatsApp chat that opened");
  return true;
}

/**
 * Downloads the PDF and opens the default mail client with a pre-filled invoice email.
 * @returns {boolean} true if intent opened; false if email missing.
 */
export function sendInvoiceViaEmail(bill, client, businessInfo) {
  const email = (client?.email || bill?.client_email || "").trim();
  if (!email) {
    toast.error("Client email is required. Add it in Edit Details.");
    return false;
  }
  generateInvoicePDF(bill, businessInfo);
  const subject = `Invoice ${bill.invoice_no} from ${businessInfo?.business_name || "Billing"}`;
  const body = buildInvoiceMessage(bill, businessInfo);
  const url = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
  window.location.href = url;
  toast.success("PDF downloaded — attach it in the email draft that opened");
  return true;
}

export function copyInvoiceMessage(bill, businessInfo) {
  const text = buildInvoiceMessage(bill, businessInfo);
  if (!navigator.clipboard) {
    toast.error("Clipboard not available in this browser");
    return false;
  }
  navigator.clipboard.writeText(text).then(
    () => toast.success("Invoice text copied to clipboard"),
    () => toast.error("Failed to copy invoice text")
  );
  return true;
}

export { buildInvoiceMessage };
